/**
 * FilesBand: the band under an open task row, which lists the case's
 * files, each openable to read. The reads fetch only while a row is open,
 * so a closed row costs nothing.
 */

import { useState } from 'react'

import { getApiErrorDetail } from '@/api/client'
import { CodeBlock } from '@/components/refinery/CodeBlock'
import { REFINERY_COPY, type TaskCase } from '@/components/refinery/rows'
import { useProvenTaskFile, useProvenTaskFiles } from '@/hooks/useProvenTasks'

function CaseFile({ task, path }: { task: TaskCase; path: string }): React.JSX.Element {
  const { data, isLoading, isError, error } = useProvenTaskFile(task, path, true)
  if (isLoading) {
    return <p className="text-xs text-muted-foreground">{REFINERY_COPY.fileLoading}</p>
  }
  if (isError || data === undefined) {
    return (
      <p className="text-xs text-destructive">
        {REFINERY_COPY.fileFailed} {getApiErrorDetail(error) ?? path}
      </p>
    )
  }
  return (
    <div className="space-y-2">
      <CodeBlock text={data.text} />
      {data.truncated && (
        <p className="text-xs text-muted-foreground">{REFINERY_COPY.fileTruncated}</p>
      )}
    </div>
  )
}

function FileRow({ task, path }: { task: TaskCase; path: string }): React.JSX.Element {
  const [open, setOpen] = useState(false)
  return (
    <li className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        <span className="min-w-0 truncate font-mono text-xs">{path}</span>
        <button
          type="button"
          onClick={() => {
            setOpen((current) => !current)
          }}
          aria-expanded={open}
          className="flex-none text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          {open ? REFINERY_COPY.hideFile : REFINERY_COPY.showFile}
        </button>
      </div>
      {open && <CaseFile task={task} path={path} />}
    </li>
  )
}

/** One task's case files, each openable to read. */
export function FilesBand({ task, id }: { task: TaskCase; id: string }): React.JSX.Element {
  const { data, isLoading, isError, error } = useProvenTaskFiles(task, true)
  return (
    <div id={id} className="border-l-2 border-border bg-muted/30 px-4 py-3">
      {isLoading && <p className="text-xs text-muted-foreground">{REFINERY_COPY.filesLoading}</p>}
      {isError && (
        <p className="text-xs text-destructive">
          {REFINERY_COPY.filesFailed} {getApiErrorDetail(error) ?? task.case}
        </p>
      )}
      {data !== undefined && (
        <ul className="space-y-2">
          {data.files.map((path) => (
            <FileRow key={path} task={task} path={path} />
          ))}
        </ul>
      )}
    </div>
  )
}
